import { Alert } from '@jmvilaca-corp/auth-ui'
import {
  useSportCoricoEvent,
  type SportCoricoChatMessage,
  type SportCoricoConvocation,
  type SportCoricoTodo,
} from '../lib/calendarSync'
import { formatMoment, timeKey } from '../lib/dates'
import { MatchComposition, MatchDay, MatchLive, MatchRanking, MatchSummary } from './SportCoricoMatchDetails'
import Tabs, { type Tab } from './Tabs'

type Answer = 'present' | 'absent' | 'waiting'

const answerLabels: Record<Answer, string> = {
  present: 'Presents',
  absent: 'Absents',
  waiting: 'Sans reponse',
}

function answerOf(convocation: SportCoricoConvocation): Answer {
  if (convocation.present) {
    return 'present'
  }

  return convocation.respondedAt === null ? 'waiting' : 'absent'
}

/**
 * Ce que SportCorico dit d'un evenement importe : rendez-vous, responsable et, pour un match, score et
 * homme du match ; puis un onglet par rubrique qui a quelque chose a montrer. Le detail est relu chez
 * SportCorico a l'ouverture, avec le compte lie du proprietaire du calendrier.
 */
export default function SportCoricoDetails({ eventId }: { eventId: string }) {
  const details = useSportCoricoEvent(eventId)

  if (details.data === undefined) {
    return details.isPending || details.error === null ? (
      <p className="text-sm text-neutral-500">Chargement des informations SportCorico...</p>
    ) : (
      <Alert tone="info">Les informations SportCorico n'ont pas pu etre lues : {details.error.message}</Alert>
    )
  }

  const { meetAt, ownerFirstName, ownerLastName, convocations, todos, chatMessages, syncedAt, detailsSyncedAt, changedAt, externalId, match } =
    details.data
  const owner = [ownerFirstName, ownerLastName].filter((part) => part !== null && part.length > 0).join(' ')

  // SportCorico ne mesure le temps de jeu que si un live a ete tenu : sans
  // aucune minute, les zeros ne veulent rien dire et ne sont pas montres.
  const minutesOf = new Map(
    (match?.playerStats ?? []).filter((stat) => (stat.minutes ?? 0) > 0).map((stat) => [stat.player, stat.minutes!]),
  )

  const tabs: Tab[] = [
    {
      key: 'convocations',
      label: 'Convoques',
      count: convocations.length,
      content: <Convocations convocations={convocations} minutesOf={minutesOf} />,
    },
  ]

  if (match !== null && match.composition !== null) {
    tabs.push({
      key: 'composition',
      label: 'Compo',
      ...(match.composition.formation === null ? {} : { count: match.composition.formation }),
      content: <MatchComposition composition={match.composition} minutesOf={minutesOf} />,
    })
  }

  if (match !== null && match.liveActions.length > 0) {
    tabs.push({ key: 'live', label: 'Live', content: <MatchLive match={match} /> })
  }

  if (match !== null && match.otherMatches.length > 0) {
    tabs.push({ key: 'matchday', label: 'Matchs de la journee', content: <MatchDay match={match} /> })
  }

  if (match !== null && match.ranking.length > 0) {
    tabs.push({ key: 'ranking', label: 'Classement', content: <MatchRanking match={match} /> })
  }

  if (todos.length > 0) {
    tabs.push({
      key: 'todos',
      label: 'Taches',
      count: `${todos.filter((todo) => todo.completed).length}/${todos.length}`,
      content: <Todos todos={todos} />,
    })
  }

  if (chatMessages.length > 0) {
    tabs.push({ key: 'chat', label: 'Chat', count: chatMessages.length, content: <Chat messages={chatMessages} /> })
  }

  return (
    <div className="space-y-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      {(meetAt !== null || owner.length > 0) && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {meetAt !== null && (
            <div className="contents">
              <dt className="font-medium text-neutral-700 dark:text-neutral-300">Rendez-vous</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{timeKey(new Date(meetAt))}</dd>
            </div>
          )}
          {owner.length > 0 && (
            <div className="contents">
              <dt className="font-medium text-neutral-700 dark:text-neutral-300">Responsable</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{owner}</dd>
            </div>
          )}
        </dl>
      )}

      {detailsSyncedAt === null ? (
        <p className="text-sm text-neutral-500">
          {details.isRefreshing
            ? 'Chargement du detail depuis SportCorico...'
            : "Le detail de cet evenement n'a pas encore ete releve chez SportCorico."}
        </p>
      ) : (
        <>
          {match !== null && <MatchSummary match={match} />}
          <Tabs label="Informations SportCorico" tabs={tabs} />
        </>
      )}

      <p className="text-xs text-neutral-500">
        {details.isRefreshing && detailsSyncedAt !== null
          ? 'Verification chez SportCorico...'
          : // SportCorico ne date pas ses modifications : la premiere date est celle ou une lecture
            // a rapporte du neuf, la seconde celle de la derniere lecture, qu'elle ait change quelque chose ou non.
            `Modifie depuis SportCorico le ${formatMoment(changedAt)} · verifie le ${formatMoment(detailsSyncedAt ?? syncedAt)}`}
        {externalId === null ? '' : ` · evenement n° ${externalId}`}
      </p>
    </div>
  )
}

function Convocations({
  convocations,
  minutesOf,
}: {
  convocations: SportCoricoConvocation[]
  minutesOf: ReadonlyMap<string, number>
}) {
  if (convocations.length === 0) {
    return <p className="text-sm text-neutral-500">Aucune convocation pour l'instant.</p>
  }

  return (
    <div className="space-y-3">
      {(['present', 'absent', 'waiting'] as const).map((answer) => {
        const group = convocations.filter((convocation) => answerOf(convocation) === answer)
        return (
          group.length > 0 && (
            <div key={answer}>
              <h4 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                {answerLabels[answer]} ({group.length})
              </h4>
              <ul className="mt-1 space-y-1">
                {group.map((convocation, index) => {
                  const name = `${convocation.firstName} ${convocation.lastName}`.trim()
                  const minutes = minutesOf.get(name)
                  return (
                    <li key={index} className="text-sm text-neutral-900 dark:text-neutral-100">
                      <span className="flex items-baseline justify-between gap-3">
                        <span>
                          {name}
                          {minutes !== undefined && (
                            <>
                              {' '}
                              <span className="text-neutral-500" title="Temps de jeu">
                                ({minutes}')
                              </span>
                            </>
                          )}
                        </span>
                        {convocation.respondedAt !== null && (
                          <span className="shrink-0 text-xs tabular-nums text-neutral-500">
                            {formatMoment(convocation.respondedAt)}
                          </span>
                        )}
                      </span>
                      {convocation.absenceJustification !== null && (
                        <span className="block text-xs text-neutral-600 dark:text-neutral-400">
                          {convocation.absenceJustification}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        )
      })}
    </div>
  )
}

function Todos({ todos }: { todos: SportCoricoTodo[] }) {
  return (
    <ul className="space-y-1">
      {todos.map((todo, index) => (
        <li key={index} className="flex items-baseline gap-2 text-sm text-neutral-900 dark:text-neutral-100">
          <span aria-hidden="true">{todo.completed ? '☑' : '☐'}</span>
          <span className={todo.completed ? 'text-neutral-500 line-through' : ''}>
            <span className="sr-only">{todo.completed ? 'Fait : ' : 'A faire : '}</span>
            {todo.title}
          </span>
          {todo.assignees.length > 0 && <span className="text-xs text-neutral-500">{todo.assignees.join(', ')}</span>}
        </li>
      ))}
    </ul>
  )
}

function Chat({ messages }: { messages: SportCoricoChatMessage[] }) {
  return (
    <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
      {messages.map((message, index) => (
        <li key={index} className="rounded-lg bg-neutral-100 px-3 py-2 text-sm dark:bg-neutral-800">
          <span className="flex items-baseline justify-between gap-3">
            <span className="font-medium text-neutral-900 dark:text-neutral-100">{message.author}</span>
            {message.sentAt !== null && (
              <span className="shrink-0 text-xs tabular-nums text-neutral-500">{formatMoment(message.sentAt)}</span>
            )}
          </span>
          <span className="block whitespace-pre-wrap break-words text-neutral-800 dark:text-neutral-200">{message.content}</span>
        </li>
      ))}
    </ul>
  )
}
